/**
 * 教育视频效果合集 Compositions
 *
 * 包含 5 个高质量教育效果：
 *   - TimelineAnimation（历史时间线动画）
 *   - QuizInteractionAnimation（互动测验动画）
 *   - DataVisualizationAnimation（数据可视化动画）
 *   - LanguageLearningAnimation（语言学习闪卡动画）
 *   - LogicPuzzleAnimation（逻辑谜题推理动画）
 *
 * @date 2026-03-18
 */

import React from 'react';
import { Composition } from 'remotion';
import {
  TimelineAnimation,
  exampleTimelineEvents,
} from '../templates/educational/TimelineAnimation';
import {
  QuizInteractionAnimation,
  exampleQuizQuestion,
} from '../templates/educational/QuizInteractionAnimation';
import {
  DataVisualizationAnimation,
  exampleDataPoints,
  examplePieData,
} from '../templates/educational/DataVisualizationAnimation';
import {
  LanguageLearningAnimation,
  exampleWordCards,
} from '../templates/educational/LanguageLearningAnimation';
import {
  LogicPuzzleAnimation,
  examplePuzzle,
} from '../templates/thinking/LogicPuzzleAnimation';
import { MathEquationAnimation } from '../templates/academic/MathEquationAnimation';
import { ExcelDataAnalysis } from '../templates/skills/ExcelDataAnalysis';
import { QuantumComputingBasics } from '../templates/popular-science/QuantumComputingBasics';
import { SolarSystemAnimation } from '../templates/popular-science/SolarSystemAnimation';
import { FinancialLiteracyAnimation } from '../templates/life-knowledge/FinancialLiteracyAnimation';
import { CognitiveBiasAnimation } from '../templates/psychology/CognitiveBiasAnimation';
import { TrigonometryWaveAnimation } from '../templates/academic/TrigonometryWaveAnimation';
import { HistoricalTimelineAnimation, exampleHistoricalEvents } from '../templates/academic/HistoricalTimelineAnimation';
import { OperantConditioningAnimation } from '../templates/psychology/OperantConditioningAnimation';
import { ClimateChangeVisualization } from '../templates/popular-science/ClimateChangeVisualization';
import { FirstAidBasicsAnimation } from '../templates/life-knowledge/FirstAidBasicsAnimation';
import { SortingAlgorithmAnimation } from '../templates/skills/SortingAlgorithmAnimation';
import { SpacedRepetitionAnimation } from '../templates/thinking/SpacedRepetitionAnimation';
import { APIFlowAnimation, exampleAPIRequests } from '../templates/skills/APIFlowAnimation';
import { TimeboxingAnimation } from '../templates/thinking/TimeboxingAnimation';
import { EvolutionTimelineAnimation } from '../templates/popular-science/EvolutionTimelineAnimation';
import { ElectromagneticSpectrumAnimation } from '../templates/popular-science/ElectromagneticSpectrumAnimation';
import { NeuralNetworkAnimation } from '../templates/popular-science/NeuralNetworkAnimation';
import { BlackHolePopSciAnimation } from '../templates/popular-science/BlackHolePopSciAnimation';
import { VaccineMechanismAnimation } from '../templates/popular-science/VaccineMechanismAnimation';
import { PersonalFinanceAnimation } from '../templates/life-knowledge/PersonalFinanceAnimation';
import { HomeMaintenanceAnimation } from '../templates/life-knowledge/HomeMaintenanceAnimation';
import { SleepScienceAnimation } from '../templates/life-knowledge/SleepScienceAnimation';
import { NutritionGuideAnimation } from '../templates/life-knowledge/NutritionGuideAnimation';
import { EmergencyPrepAnimation } from '../templates/life-knowledge/EmergencyPrepAnimation';
import { CognitiveDistortionsAnimation } from '../templates/psychology/CognitiveDistortionsAnimation';

export const EducationalEffectsCompositions: React.FC = () => {
  return (
    <>
      {/* ==================== 1. 时间线动画效果 ==================== */}
      <Composition
        id="TimelineAnimation"
        component={TimelineAnimation as React.FC}
        durationInFrames={240}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          events: exampleTimelineEvents,
          primaryColor: '#4A90E2',
          secondaryColor: '#7B68EE',
          backgroundColor: '#0F1419',
          showProgress: true,
        }}
      />

      {/* 时间线动画 - 科技主题变体 */}
      <Composition
        id="TimelineAnimationTech"
        component={TimelineAnimation as React.FC}
        durationInFrames={240}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          events: [
            {
              year: '1991',
              title: 'Web 诞生',
              description: 'Tim Berners-Lee 发明万维网',
            },
            {
              year: '1998',
              title: 'Google 成立',
              description: '搜索引擎改变信息获取方式',
            },
            {
              year: '2004',
              title: 'Facebook 上线',
              description: '社交网络时代开启',
            },
            {
              year: '2007',
              title: 'iPhone 发布',
              description: '智能手机革命',
            },
            {
              year: '2016',
              title: 'AlphaGo 获胜',
              description: 'AI 击败围棋世界冠军',
            },
            {
              year: '2022',
              title: 'ChatGPT 爆发',
              description: '生成式 AI 走向大众',
            },
          ],
          primaryColor: '#06B6D4',
          secondaryColor: '#8B5CF6',
          backgroundColor: '#0A0E27',
          showProgress: true,
        }}
      />

      {/* ==================== 2. 互动测验动画效果 ==================== */}
      <Composition
        id="QuizInteractionAnimation"
        component={QuizInteractionAnimation as React.FC}
        durationInFrames={150}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          question: exampleQuizQuestion,
          showAnswerAfter: 90,
          primaryColor: '#4A90E2',
          correctColor: '#10B981',
          wrongColor: '#EF4444',
          backgroundColor: '#0F1419',
        }}
      />

      {/* 互动测验 - 科学知识变体 */}
      <Composition
        id="QuizScienceQuestion"
        component={QuizInteractionAnimation as React.FC}
        durationInFrames={150}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          question: {
            question: '地球绕太阳一周需要多长时间？',
            hint: '想想一年有多少天',
            options: [
              {
                text: '24 小时',
                isCorrect: false,
                explanation: '24 小时是地球自转一周的时间',
              },
              {
                text: '365.25 天',
                isCorrect: true,
                explanation: '地球公转周期约为 365.25 天，因此每 4 年有一个闰年',
              },
              {
                text: '30 天',
                isCorrect: false,
                explanation: '30 天只是一个月的平均长度',
              },
              {
                text: '12 小时',
                isCorrect: false,
                explanation: '12 小时是半天',
              },
            ],
          },
          showAnswerAfter: 90,
          primaryColor: '#10B981',
          correctColor: '#10B981',
          wrongColor: '#EF4444',
          backgroundColor: '#064E3B',
        }}
      />

      {/* ==================== 3. 数据可视化动画效果 ==================== */}
      
      {/* 柱状图 */}
      <Composition
        id="DataVisualizationBar"
        component={DataVisualizationAnimation as React.FC}
        durationInFrames={180}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          title: '年度增长趋势',
          subtitle: '2020-2024 年业务增长数据',
          data: exampleDataPoints,
          chartType: 'bar',
          primaryColor: '#4A90E2',
          backgroundColor: '#0F1419',
          showValues: true,
        }}
      />

      {/* 折线图 */}
      <Composition
        id="DataVisualizationLine"
        component={DataVisualizationAnimation as React.FC}
        durationInFrames={180}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          title: '用户增长曲线',
          subtitle: '月度活跃用户变化趋势',
          data: [
            { label: '1月', value: 1200 },
            { label: '2月', value: 1580 },
            { label: '3月', value: 2100 },
            { label: '4月', value: 2890 },
            { label: '5月', value: 3200 },
            { label: '6月', value: 4150 },
          ],
          chartType: 'line',
          primaryColor: '#8B5CF6',
          backgroundColor: '#0F1419',
          showValues: true,
        }}
      />

      {/* 饼图 */}
      <Composition
        id="DataVisualizationPie"
        component={DataVisualizationAnimation as React.FC}
        durationInFrames={180}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          title: '市场份额分布',
          subtitle: '各业务线收入占比',
          data: examplePieData,
          chartType: 'pie',
          primaryColor: '#EC4899',
          backgroundColor: '#0F1419',
          showValues: true,
        }}
      />

      {/* ==================== 4. 语言学习闪卡动画效果 ==================== */}
      
      {/* 英语单词记忆 */}
      <Composition
        id="LanguageLearningFlashcard"
        component={LanguageLearningAnimation}
        durationInFrames={600}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          words: exampleWordCards,
          primaryColor: '#FF6B35',
          secondaryColor: '#4ECDC4',
          backgroundColor: '#0A0E27',
        }}
      />

      {/* TOEFL 词汇学习 */}
      <Composition
        id="LanguageLearningTOEFL"
        component={LanguageLearningAnimation}
        durationInFrames={600}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          words: [
            {
              word: 'Ubiquitous',
              phonetic: '/juːˈbɪkwɪtəs/',
              meaning: '无处不在的',
              example: 'Smartphones have become ubiquitous in modern society.',
              difficulty: 'hard',
            },
            {
              word: 'Pragmatic',
              phonetic: '/præɡˈmætɪk/',
              meaning: '务实的，实用主义的',
              example: 'We need a pragmatic approach to solve this problem.',
              difficulty: 'medium',
            },
          ],
          primaryColor: '#667EEA',
          secondaryColor: '#764BA2',
          backgroundColor: '#1A1F3A',
        }}
      />

      {/* ==================== 5. 逻辑谜题推理动画效果 ==================== */}
      
      {/* 经典逻辑推理题 */}
      <Composition
        id="LogicPuzzleReasoning"
        component={LogicPuzzleAnimation}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          puzzle: examplePuzzle,
          primaryColor: '#FF6B35',
          secondaryColor: '#4ECDC4',
          backgroundColor: '#0F1419',
        }}
      />

      {/* 数学逻辑题 */}
      <Composition
        id="LogicPuzzleMath"
        component={LogicPuzzleAnimation}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          puzzle: {
            question: '谁说的假话？',
            suspects: ['小明', '小红', '小刚', '小丽'],
            clues: [
              { text: '只有一人说假话', icon: '🎯' },
              { text: '小明: 我考了第一名', icon: '1️⃣' },
              { text: '小红: 小明在说谎', icon: '⚠️' },
              { text: '小刚: 我考了第二名', icon: '2️⃣' },
              { text: '小丽: 小红说了真话', icon: '✓' },
            ],
            steps: [
              {
                premise: '假设小明说真话',
                conclusion: '→ 小明第一名',
              },
              {
                premise: '则小红说谎',
                conclusion: '→ 小明没说谎 ✓',
              },
              {
                premise: '小丽说真话',
                conclusion: '→ 小红说真话',
              },
              {
                premise: '矛盾！',
                conclusion: '假设错误',
                highlight: 'error',
              },
              {
                premise: '小明说谎',
                conclusion: '→ 小红说真话',
              },
              {
                premise: '最终答案',
                conclusion: '小明说了假话',
                highlight: 'answer',
              },
            ],
          },
          primaryColor: '#F59E0B',
          secondaryColor: '#10B981',
          backgroundColor: '#064E3B',
        }}
      />
      {/* ==================== 6. 数学公式动画效果 ==================== */}
      
      {/* 二次方程求解 */}
      <Composition
        id="MathEquationQuadratic"
        component={MathEquationAnimation}
        durationInFrames={600}
        fps={30}
        width={1920}
        height={1080}
      />

      {/* ==================== 7. 心流状态可视化效果 ==================== */}
      
      {/* ==================== 7. Excel 数据分析教程效果 ==================== */}
      
      {/* Excel 数据分析教程 */}
      <Composition
        id="ExcelDataAnalysis"
        component={ExcelDataAnalysis}
        durationInFrames={240}
        fps={30}
        width={1920}
        height={1080}
      />

      {/* ==================== 10. 量子计算基础效果 ==================== */}
      
      {/* 量子计算基础可视化 */}
      <Composition
        id="QuantumComputingBasics"
        component={QuantumComputingBasics}
        durationInFrames={240}
        fps={30}
        width={1920}
        height={1080}
      />

      {/* ==================== 11. 番茄工作法可视化效果 ==================== */}
      
      {/* ==================== 11. 太阳系行星运动效果 ==================== */}
      <Composition
        id="SolarSystemAnimation"
        component={SolarSystemAnimation}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
      />

      <Composition
        id="NeuralNetworkAnimation"
        component={NeuralNetworkAnimation}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
      />

      <Composition
        id="BlackHolePopSciAnimation"
        component={BlackHolePopSciAnimation}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
      />

      <Composition
        id="VaccineMechanismAnimation"
        component={VaccineMechanismAnimation}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
      />

      {/* ==================== 16. 财商基础知识效果 ==================== */}
      <Composition
        id="FinancialLiteracyAnimation"
        component={FinancialLiteracyAnimation}
        durationInFrames={240}
        fps={30}
        width={1920}
        height={1080}
      />

      {/* ==================== 17. 认知偏差可视化效果 ==================== */}
      <Composition
        id="CognitiveBiasAnimation"
        component={CognitiveBiasAnimation}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
      />

      {/* ==================== 19. 气候变化数据可视化效果 ==================== */}
      <Composition
        id="ClimateChangeVisualization"
        component={ClimateChangeVisualization}
        durationInFrames={300}
        fps={30}
        width={1920}
        height={1080}
      />

      {/* ==================== 22. 急救基础知识动画效果 ==================== */}
      <Composition
        id="FirstAidBasicsAnimation"
        component={FirstAidBasicsAnimation}
        durationInFrames={300}
        fps={30}
        width={1920}
        height={1080}
      />

      {/* ==================== 23. 排序算法可视化效果 ==================== */}
      <Composition
        id="SortingAlgorithmAnimation"
        component={SortingAlgorithmAnimation}
        durationInFrames={360}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          title: '冒泡排序算法',
          subtitle: 'Bubble Sort Visualization',
          algorithm: 'bubble',
          initialArray: [64, 34, 25, 12, 22, 11, 90],
          primaryColor: '#4A90E2',
          secondaryColor: '#F59E0B',
          sortedColor: '#10B981',
          backgroundColor: '#0F1419',
          showCode: true,
        }}
      />

      {/* 排序算法 - 快速排序示例 */}
      <Composition
        id="SortingAlgorithmQuickSort"
        component={SortingAlgorithmAnimation}
        durationInFrames={360}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          title: '快速排序算法',
          subtitle: 'Quick Sort Visualization',
          algorithm: 'bubble',
          initialArray: [38, 27, 43, 3, 9, 82, 10],
          primaryColor: '#8B5CF6',
          secondaryColor: '#EC4899',
          sortedColor: '#10B981',
          backgroundColor: '#1A1F3A',
          showCode: true,
        }}
      />

      {/* ==================== 25. 艾宾浩斯遗忘曲线效果 ==================== */}
      <Composition
        id="SpacedRepetitionAnimation"
        component={SpacedRepetitionAnimation}
        durationInFrames={480}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          title: '艾宾浩斯遗忘曲线',
          subtitle: 'Ebbinghaus Forgetting Curve',
          showCurve: true,
          primaryColor: '#4A90E2',
          accentColor: '#F59E0B',
          backgroundColor: '#0F1419',
        }}
      />

      {/* 间隔重复 - 垂直版 */}
      <Composition
        id="SpacedRepetitionVertical"
        component={SpacedRepetitionAnimation}
        durationInFrames={480}
        fps={30}
        width={1080}
        height={1920}
        defaultProps={{
          title: '科学复习方法',
          subtitle: '间隔重复学习法',
          showCurve: true,
          primaryColor: '#10B981',
          accentColor: '#06B6D4',
          backgroundColor: '#064E3B',
        }}
      />

      {/* ==================== 26. 操作性条件反射动画效果 ==================== */}
      <Composition
        id="OperantConditioningAnimation"
        component={OperantConditioningAnimation}
        durationInFrames={330}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          primaryColor: '#4A90E2',
          secondaryColor: '#FF6B6B',
          backgroundColor: '#0f1419',
        }}
      />

      {/* ==================== 29. 三角函数波形动画 ==================== */}
      <Composition
        id="TrigonometryWaveAnimation"
        component={TrigonometryWaveAnimation}
        durationInFrames={450}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          primaryColor: '#00d9ff',
          secondaryColor: '#ff6b6b',
          accentColor: '#ffd93d',
          backgroundColor: '#0a0e27',
          showSin: true,
          showCos: true,
          showTan: false,
          amplitude: 1,
          frequency: 1,
          phase: 0,
        }}
      />

      {/* 三角函数波形 - Tan 函数版 */}
      <Composition
        id="TrigonometryWaveAnimationWithTan"
        component={TrigonometryWaveAnimation}
        durationInFrames={450}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          primaryColor: '#00d9ff',
          secondaryColor: '#ff6b6b',
          accentColor: '#ffd93d',
          backgroundColor: '#0a0e27',
          showSin: true,
          showCos: true,
          showTan: true,
          amplitude: 1,
          frequency: 0.8,
          phase: 0,
        }}
      />

      {/* ==================== 36. 历史时间轴动画 ==================== */}
      <Composition
        id="HistoricalTimelineAnimation"
        component={HistoricalTimelineAnimation}
        durationInFrames={450}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          events: exampleHistoricalEvents,
          primaryColor: '#00d9ff',
          secondaryColor: '#ff6b6b',
          accentColor: '#ffd93d',
          backgroundColor: '#0a0e27',
          title: '人类文明历程',
          subtitle: 'Major Events in Human History',
        }}
      />

      {/* 历史时间轴 - 科技发展史 */}
      <Composition
        id="HistoricalTimelineTechHistory"
        component={HistoricalTimelineAnimation}
        durationInFrames={450}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          events: [
            {
              year: '1440',
              title: '印刷术发明',
              description: '古登堡印刷术推动知识传播',
              icon: '📖',
              category: '科技',
            },
            {
              year: '1760',
              title: '工业革命',
              description: '蒸汽机开启机械化时代',
              icon: '⚙️',
              category: '工业',
            },
            {
              year: '1879',
              title: '电灯发明',
              description: '爱迪生点亮世界',
              icon: '💡',
              category: '科技',
            },
            {
              year: '1903',
              title: '飞机发明',
              description: '莱特兄弟实现人类飞行梦想',
              icon: '✈️',
              category: '交通',
            },
            {
              year: '1946',
              title: '计算机诞生',
              description: 'ENIAC 开启数字时代',
              icon: '💻',
              category: '科技',
            },
            {
              year: '1991',
              title: 'Web 诞生',
              description: '互联网改变世界',
              icon: '🌐',
              category: '科技',
            },
          ],
          primaryColor: '#06B6D4',
          secondaryColor: '#8B5CF6',
          accentColor: '#F59E0B',
          backgroundColor: '#0A0E27',
          title: '科技发展历程',
          subtitle: 'History of Technology',
        }}
      />

      {/* ==================== 37. 个人理财动画 ==================== */}
      <Composition
        id="PersonalFinanceAnimation"
        component={PersonalFinanceAnimation}
        durationInFrames={300}
        fps={30}
        width={1920}
        height={1080}
      />

      {/* ==================== 38. 家庭维护清单动画 ==================== */}
      <Composition
        id="HomeMaintenanceAnimation"
        component={HomeMaintenanceAnimation}
        durationInFrames={300}
        fps={30}
        width={1920}
        height={1080}
      />

      <Composition
        id="SleepScienceAnimation"
        component={SleepScienceAnimation}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
      />

      <Composition
        id="NutritionGuideAnimation"
        component={NutritionGuideAnimation}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
      />

      <Composition
        id="EmergencyPrepAnimation"
        component={EmergencyPrepAnimation}
        durationInFrames={540}
        fps={30}
        width={1920}
        height={1080}
      />

      {/* ==================== 39. 认知扭曲动画 ==================== */}
      <Composition
        id="CognitiveDistortionsAnimation"
        component={CognitiveDistortionsAnimation}
        durationInFrames={360}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          primaryColor: '#FF6B35',
          secondaryColor: '#4ECDC4',
          backgroundColor: '#0A0E27',
        }}
      />

      {/* ==================== 41. API 调用流程动画效果 ==================== */}
      <Composition
        id="APIFlowAnimation"
        component={APIFlowAnimation}
        durationInFrames={270}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          primaryColor: '#4A90E2',
          secondaryColor: '#10B981',
          backgroundColor: '#0F1419',
          requestConfig: exampleAPIRequests.getUser,
        }}
      />

      {/* API 流程 - POST 请求示例 */}
      <Composition
        id="APIFlowAnimationPOST"
        component={APIFlowAnimation}
        durationInFrames={270}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          primaryColor: '#4A90E2',
          secondaryColor: '#10B981',
          backgroundColor: '#0F1419',
          requestConfig: exampleAPIRequests.createUser,
        }}
      />


      {/* ==================== 38. 时间盒工作法动画 ==================== */}
      <Composition
        id="TimeboxingAnimation"
        component={TimeboxingAnimation}
        durationInFrames={270}
        fps={30}
        width={1920}
        height={1080}
        defaultProps={{
          primaryColor: '#8B5CF6',
          secondaryColor: '#EF4444',
          backgroundColor: '#0A0E27',
        }}
      />
    </>
  );
};